import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import path from 'path';
import { get as getValueAtPath } from 'lodash';
import { createHashHistory } from 'history';
import { Container, Row, Col, Button, Form, ButtonGroup } from 'reactstrap';
import { Prompt } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { editPermissionSelector, viewPermissionSelector } from '../common/classes/permissions';
import { removeItemWithConfirm } from '../common/classes/handleItemWithConfirm';
import AnnotationEditorDetailViewPanel from './annotationEditorDetailViewPanel';
import annTools from '../common/pdf/annotations/commonAnnotationTools';
import entityComposition from '../common/classes/entityComposition';
import PopoverButton from '../common/controls/popoverButton';
import routeHelper from '../common/classes/routeHelper';
import SaveButton from '../common/controls/saveButton';
import IssueEditor from '../issues/issueEditor';
import navigation from '../common/pdf/navigation';
import Icon from '../common/containers/icon';
import Loader from '../common/loader';
import PDF from '../common/pdf/pdf';
import create from './create';
import { DEFAULT_COLOR } from '../../reducers/annotationSelectionReducer';
import store from '../../reducers/storeRegistration';
const hashHistory = createHashHistory();
const ReportAnnotationEditor = _ref => {
  let _ref$annotationSelect = _ref.annotationSelection,
    selectableType = _ref$annotationSelect.selectableType,
    editMode = _ref$annotationSelect.editMode,
    defaultColor = _ref$annotationSelect.defaultColor,
    points = _ref$annotationSelect.points,
    _ref$annotationSelect2 = _ref.annotationSelectionActions,
    batchChange = _ref$annotationSelect2.batchChange,
    setAnnotationContext = _ref$annotationSelect2.setAnnotationContext,
    clearSelection = _ref$annotationSelect2.clearSelection,
    select = _ref$annotationSelect2.select,
    setSelectableType = _ref$annotationSelect2.setSelectableType,
    setDefaultColor = _ref$annotationSelect2.setDefaultColor,
    match = _ref.match,
    documentsButtonIndexQuery = _ref.documentsButtonIndexQuery,
    documentsButtonList = _ref.documentsButtonList,
    issuePopoverReportDocumentActions = _ref.issuePopoverReportDocumentActions,
    issue = _ref.issue,
    issueActions = _ref.issueActions,
    issueType = _ref.issueType,
    issueTypeActions = _ref.issueTypeActions,
    reportDocument = _ref.reportDocument,
    reportDocumentActions = _ref.reportDocumentActions,
    _ref$reportAnnotation = _ref.reportAnnotation,
    reportAnnotation = _ref$reportAnnotation === void 0 ? {
      loading: false,
      pendingChanges: false,
      list: [],
      active: {}
    } : _ref$reportAnnotation,
    _ref$reportAnnotation2 = _ref.reportAnnotationActions,
    selectByAnnotationTypeID = _ref$reportAnnotation2.selectByAnnotationTypeID,
    serverDeleteAnnotation = _ref$reportAnnotation2.serverDeleteAnnotation,
    serverSave = _ref$reportAnnotation2.serverSave,
    serverCreate = _ref$reportAnnotation2.serverCreate,
    undoChanges = _ref$reportAnnotation2.undoChanges,
    reset = _ref$reportAnnotation2.reset,
    reportTypeIssueType = _ref.reportTypeIssueType,
    reportTypeIssueTypeActions = _ref.reportTypeIssueTypeActions,
    userID = _ref.userID,
    canEditIssue = _ref.canEditIssue,
    canViewIssue = _ref.canViewIssue,
    notificationActions = _ref.notificationActions;
  const params = match.params;
  if (!params || !params.reportDocumentID) {
    throw new Error('props.match.params.reportDocumentID is required');
  }
  const _useState = useState(1200),
    _useState2 = _slicedToArray(_useState, 2),
    maxHeight = _useState2[0],
    setMaxHeight = _useState2[1];

  // Update dimensions when window is resized
  const updateDimensions = useCallback(() => {
    // footer is 30 + 20
    // header is 59 + 30
    const verticalMargin = 139;
    if (global.window.innerHeight < 2 * verticalMargin) {
      setMaxHeight(verticalMargin);
    } else {
      setMaxHeight(global.window.innerHeight - verticalMargin);
    }
  }, []);
  const handleClearSelection = useCallback(() => {
    issueActions.showCancel();
    clearSelection();
  }, [issueActions, clearSelection]);
  const handleSave = useCallback(() => {
    // NOTE: Using store.getState() to avoid React stale closure issues.
    // Callbacks can be created with stale props even after component re-renders.
    // Reading from store at execution time ensures we get current state.
    const currentState = store.getState();
    const currentReportAnnotation = currentState.reportAnnotation;
    const currentIssue = currentState.issue;
    if (currentReportAnnotation.pendingChanges) {
      // Kicking off a serverSave for report annotation will automatically
      // kick off an issue save if there are pending changes
      serverSave(currentReportAnnotation.active);
    } else if (currentIssue.pendingChanges) {
      const issueVersion = currentIssue.active && currentIssue.active.content && currentIssue.active.content.version || undefined;
      issueActions.serverSave(issueVersion);
    }
  }, [serverSave, issueActions]);
  const getIssueType = useCallback(reportTypeID => {
    // Get all reportType -> issueType entries that match our current reportType
    const possibleTypes = reportTypeIssueType.list.filter(join => join.reportTypeID === reportTypeID);

    // For the default/fallback value, we need the first issueType on the list, so we've got to sort
    const sortedIssueTypeList = [...issueType.list].sort((left, right) => left.sortOrder - right.sortOrder);

    // Always want to default to returning an issueType, if possible return the
    // issueType that's paired with the reportType
    return possibleTypes.length ? sortedIssueTypeList.find(iType => iType.id === possibleTypes[0].issueTypeID) || sortedIssueTypeList[0] : sortedIssueTypeList[0];
  }, [reportTypeIssueType.list, issueType.list]);

  // blocks save with a notification prompt when reportAnnotation or issue have pendingChanges
  const handleTransition = useCallback(function () {
    let proceed = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : () => {};
    // NOTE: Using store.getState() to avoid React stale closure issues.
    // Callbacks can be created with stale props even after component re-renders.
    // Reading from store at execution time ensures we get current state.
    const currentState = store.getState();
    const currentReportAnnotation = currentState.reportAnnotation;
    const currentIssue = currentState.issue;
    const reportAnnotationLoading = currentReportAnnotation.loading,
      reportAnnotationPendingChanges = currentReportAnnotation.pendingChanges;
    const error = currentIssue.error,
      issueLoading = currentIssue.loading,
      issuePendingChanges = currentIssue.pendingChanges;
    const entityIsMissing = error && error.response && error.response.status === 410;

    // do not allow transitions when ReportAnnotation or Issue data is loading.
    if (reportAnnotationLoading || issueLoading) {
      const notificationID = 'transition_while_loading';
      notificationActions.dismiss(notificationID);
      notificationActions.info({
        message: 'Please wait for data to finish loading before transitioning away.',
        id: notificationID
      }, {
        autoClose: 2000 // ensures only one notification at a time
      });
      return false; // tells Prompt to block navigation
    }
    if (reportAnnotationPendingChanges || issuePendingChanges) {
      const noHandler = () => {
        if (reportAnnotationPendingChanges) {
          undoChanges();
        }
        if (issuePendingChanges) {
          issueActions.undoChanges();
        }

        // clear active selection after undoing changes
        handleClearSelection();
        proceed();
      };
      if (entityIsMissing) {
        noHandler();
        return true;
      }
      hashHistory.replace(match.url); // block navigation
      routeHelper.displayModal(handleSave, noHandler);
      return false; // tells Prompt to block navigation
    }

    // clear active selection
    handleClearSelection();
    proceed();
    return true; // tells Prompt to allow navigation
  }, [notificationActions, handleClearSelection, undoChanges, issueActions, match.url, handleSave]);
  const handleCreation = useCallback(clickDetails => {
    const pageNumber = clickDetails.pageNumber,
      rotation = clickDetails.rotation,
      xLocation = clickDetails.xLocation,
      yLocation = clickDetails.yLocation,
      strokeWidth = clickDetails.strokeWidth,
      rectString = clickDetails.rectString,
      rectsString = clickDetails.rectsString,
      clickPoints = clickDetails.points;
    const newAnnotations = [];
    const list = reportAnnotation.list;
    const reportDocumentID = params.reportDocumentID;
    if (selectableType === 'issue') {
      const foundIssueType = getIssueType(reportDocument.reportTypeID);
      if (!foundIssueType) {
        return;
      }
      const issueTypeID = foundIssueType.id;
      if (issueTypeID) {
        // saved radius is 15px, full size in the rectString should be 30,30
        const issueRadius = 15;
        const xLocWithOffset = xLocation - issueRadius;
        const yLocWithOffset = yLocation - issueRadius;
        const baseAnnotation = _objectSpread(_objectSpread({}, create.basicAnnotation(pageNumber, rotation, userID, defaultColor, reportDocumentID)), {}, {
          rectString: create.rectString(xLocWithOffset, yLocWithOffset)
        });
        let version;
        if (foundIssueType.formatInput) {
          let formatInput = foundIssueType.formatInput;
          if (typeof formatInput === 'string') {
            formatInput = JSON.parse(formatInput);
          }
          var _formatInput = formatInput;
          version = _formatInput.version;
        }
        const typeAnnotation = _objectSpread(_objectSpread({}, create.typedAnnotation(baseAnnotation.id, userID)), {}, {
          label: create.label(list).toString(),
          color: annTools.toRGBAObject(defaultColor),
          issueTypeID,
          version
        });
        newAnnotations.push({
          baseAnnotation,
          typeAnnotation
        });
      }
    } else if (selectableType === 'info') {
      // saved radius is 15px, full size in the rectString should be 30,30
      const infoRadius = 15;
      const xLocWithOffset = xLocation - infoRadius;
      const yLocWithOffset = yLocation - infoRadius;
      const baseAnnotation = _objectSpread(_objectSpread({}, create.basicAnnotation(pageNumber, rotation, userID, defaultColor, reportDocumentID)), {}, {
        rectString: create.rectString(xLocWithOffset, yLocWithOffset)
      });
      const typeAnnotation = create.typedAnnotation(baseAnnotation.id, userID);
      newAnnotations.push({
        baseAnnotation,
        typeAnnotation
      });
    } else if (selectableType === 'ink' || selectableType === 'line') {
      // Use points from click details if available, otherwise fall back to state
      const pointsToUse = clickPoints || points;
      const drawnAnnotationArrays = annTools.groupPointsByPage(pointsToUse);
      drawnAnnotationArrays.forEach(pts => {
        if (create.minimumSpecBool(selectableType, pts)) {
          const _pts$ = pts[0],
            annPageNumber = _pts$.pageNumber,
            annRotation = _pts$.rotation;
          const truePath = selectableType === 'line' ? [pts[0], pts[pts.length - 1]] : pts;
          const pathString = create.pathStringFromCanvasCoords(truePath, annPageNumber);
          const baseAnnotation = _objectSpread(_objectSpread({}, create.basicAnnotation(pageNumber, rotation, userID, defaultColor, reportDocumentID)), {}, {
            rectString: annTools.recalcRectStringFromPath(pathString),
            pageNumber: annPageNumber,
            rotation: annRotation
          });
          const typeAnnotation = _objectSpread(_objectSpread({}, create.typedAnnotation(baseAnnotation.id, userID)), {}, {
            pathString,
            width: strokeWidth,
            unsmoothed: selectableType === 'line' ? 1 : 0
          });
          newAnnotations.push({
            baseAnnotation,
            typeAnnotation
          });
        }
      });
    } else if (selectableType === 'freeText') {
      const boxHeight = 60;
      const boxWidth = 60;
      // +2 and -boxHeight makes the text box upper left corner line up w/the pointer of the cursor
      const xLocWithOffset = xLocation + 2;
      const yLocWithOffset = yLocation - boxHeight;
      const baseAnnotation = _objectSpread(_objectSpread({}, create.basicAnnotation(pageNumber, rotation, userID, defaultColor, reportDocumentID)), {}, {
        rectString: create.rectString(xLocWithOffset, yLocWithOffset, boxWidth, boxHeight)
      });
      const typeAnnotation = _objectSpread(_objectSpread({}, create.typedAnnotation(baseAnnotation.id, userID)), {}, {
        fontSize: 14,
        fontName: 'Helvetica'
      });
      newAnnotations.push({
        baseAnnotation,
        typeAnnotation
      });
    } else if (selectableType === 'highlight') {
      const baseAnnotation = _objectSpread(_objectSpread({}, create.basicAnnotation(pageNumber, rotation, userID, defaultColor, reportDocumentID)), {}, {
        rectString
      });
      const typeAnnotation = _objectSpread(_objectSpread({}, create.typedAnnotation(baseAnnotation.id, userID)), {}, {
        rectsString
      });
      newAnnotations.push({
        baseAnnotation,
        typeAnnotation
      });
    }
    serverCreate(newAnnotations, selectableType);
  }, [reportAnnotation, params, reportDocument.reportTypeID, userID, getIssueType, serverCreate, selectableType, defaultColor, points]);
  const handleAnnotationClick = useCallback((selectedType, clickedAnnotation, clickDetails) => {
    const active = reportAnnotation.active;
    const currentActiveIssueID = getValueAtPath(issue, 'active.id', null);

    // Handle stage click (deselection)
    if (selectedType === 'stage') {
      // If we're in edit mode with a selectable type and have click details,
      // this is a creation attempt
      if (editMode && selectableType && clickDetails) {
        handleTransition(() => {
          // Clear any existing active annotation first
          if (active && active.id) {
            if (currentActiveIssueID) {
              issueActions.showCancel();
            }
            handleClearSelection();
          } else if (selectableType === 'issue') {
            // Only proceed with creation if there's no active annotation
            const foundIssueType = getIssueType(reportDocument.reportTypeID);
            if (!foundIssueType) {
              return;
            }
            if (canViewIssue) {
              handleCreation(clickDetails);
            } else {
              notificationActions.error({
                message: 'You do not have permission to add an Issue Annotation'
              });
            }
          } else if (create.validAnnotationTypeList.includes(selectableType)) {
            handleCreation(clickDetails);
          }
        });
      } else {
        // Regular stage click - just clear selection
        handleTransition(() => {
          if (currentActiveIssueID) {
            issueActions.showCancel();
            handleClearSelection();
          }
        });
      }
      return;
    }

    // Handle annotation selection
    if (clickedAnnotation) {
      handleTransition(() => {
        if (selectedType === 'issue') {
          // Only allow selection if issue type is currently selected in the annotation bar
          if (selectableType !== 'issue') {
            return;
          }
          const issueID = clickedAnnotation.issue.id;
          selectByAnnotationTypeID(issueID);
          // Don't change selectableType when selecting an issue
          issueActions.serverShow(issueID);
        } else {
          select(clickedAnnotation);
        }
      });
    } else if (selectedType !== selectableType) {
      handleTransition(() => {
        // Clear any existing active annotation first
        if (active && active.id) {
          if (currentActiveIssueID) {
            issueActions.showCancel();
          }
          handleClearSelection();
        }

        // If we're in edit mode with click details, proceed with creation
        if (editMode && clickDetails && selectableType) {
          if (selectableType === 'issue') {
            const foundIssueType = getIssueType(reportDocument.reportTypeID);
            if (!foundIssueType) {
              return;
            }
            if (canViewIssue) {
              handleCreation(clickDetails);
            } else {
              notificationActions.error({
                message: 'You do not have permission to add an Issue Annotation'
              });
            }
          } else if (create.validAnnotationTypeList.includes(selectableType)) {
            handleCreation(clickDetails);
          }
        }
      });
    }
  }, [handleClearSelection, reportAnnotation, issue, handleTransition, canViewIssue, issueActions, notificationActions, handleCreation, reportDocument.reportTypeID, getIssueType, editMode, selectableType, select, selectByAnnotationTypeID]);

  // Add effect to handle selectableType changes
  useEffect(() => {
    if (selectableType === 'issue') {
      const currentActiveIssueID = getValueAtPath(issue, 'active.id', null);
      if (currentActiveIssueID) {
        selectByAnnotationTypeID(currentActiveIssueID);
      }
    }
  }, [selectableType, issue, selectByAnnotationTypeID]);

  // Add cleanup effect
  useEffect(() => () => {
    // selection persists, sometimes to a new report doc if not cleared
    reportDocumentActions.reset();
    reset();
    issueActions.showCancel();
  }, [reportDocumentActions, reset, issueActions]);
  const handleChange = useCallback((contentPath, value) => {
    const active = reportAnnotation.active;
    batchChange([{
      id: active.id,
      path: contentPath,
      data: value
    }]);
  }, [reportAnnotation, batchChange]);
  const handleEditReportClick = useCallback(() => {
    handleTransition(() => {
      const parentReportID = reportDocument.parentReportID;
      if (parentReportID) {
        const url = path.join('/reports/edit', parentReportID, 'AssociatedDocuments');
        hashHistory.push(url);
      }
    });
  }, [handleTransition, reportDocument]);
  const handleViewReportClick = useCallback(() => {
    handleTransition(() => {
      if (documentsButtonList[0]) {
        const reportID = documentsButtonList[0].reportID;
        const url = path.join('/reports', reportID);
        hashHistory.push(url);
      }
    });
  }, [handleTransition, documentsButtonList]);
  const handleReportDocumentClick = useCallback(_ref2 => {
    let id = _ref2.id;
    handleTransition(() => {
      setSelectableType(null);
      setDefaultColor(DEFAULT_COLOR);
      const url = path.join('/reportDocuments/edit', id);
      reportDocumentActions.reset();
      reset();
      reportDocumentActions.serverRenderReportDocument(id);
      hashHistory.push(url);
    });
  }, [handleTransition, reportDocumentActions, reset, setSelectableType, setDefaultColor]);
  const handlePageNavigation = useCallback(() => {
    const _reportAnnotation$act = reportAnnotation.active,
      annotation = _reportAnnotation$act === void 0 ? null : _reportAnnotation$act;
    if (annotation && annotation.id) {
      navigation.scrollAnnotationIntoView(annotation);
    }
  }, [reportAnnotation]);

  // Track the last reportDocumentID we fetched to prevent duplicate calls
  const lastFetchedReportDocumentIDRef = useRef(null);

  // Extract reportDocumentID to use in dependency arrays for performance
  // This prevents effects from running when other params change
  const reportDocumentID = params.reportDocumentID;

  // Effect to fetch report document - only depends on reportDocumentID
  useEffect(() => {
    // Don't fetch if we already fetched this reportDocumentID
    if (lastFetchedReportDocumentIDRef.current !== reportDocumentID) {
      lastFetchedReportDocumentIDRef.current = reportDocumentID;
      reportDocumentActions.serverRenderReportDocument(reportDocumentID);
    }
  }, [reportDocumentID, reportDocumentActions]);

  // Cleanup effect - only runs on unmount
  useEffect(() => () => {
    reportDocumentActions.reset();
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures this only runs on unmount

  // Effect to fetch other data and set up dimensions
  useEffect(() => {
    reportTypeIssueTypeActions.serverIndex(reportTypeIssueType.indexQuery);
    // Grab the report document list to populate the popover button
    issuePopoverReportDocumentActions.serverIndex(_objectSpread(_objectSpread({}, documentsButtonIndexQuery), {}, {
      filters: {
        reportDocument: [{
          id: reportDocumentID
        }]
      }
    }));
    issueTypeActions.serverIndex(issueType.indexQuery);
    updateDimensions();
    global.window.addEventListener('resize', updateDimensions);

    // Cleanup function
    return () => {
      global.window.removeEventListener('resize', updateDimensions);
    };
  }, [reportDocumentID, reportTypeIssueTypeActions, reportTypeIssueType.indexQuery, issuePopoverReportDocumentActions, documentsButtonIndexQuery, issueTypeActions, issueType.indexQuery, updateDimensions]);
  useEffect(() => {
    setAnnotationContext('report');
  }, [setAnnotationContext]);
  useEffect(() => {
    if (setSelectableType) {
      setSelectableType(null);
    }
    if (setDefaultColor) {
      setDefaultColor(DEFAULT_COLOR);
    }
  }, [setSelectableType, setDefaultColor]);

  // Render logic
  const error = reportDocument.error,
    pdfPath = reportDocument.pdfPath,
    displayName = reportDocument.displayName,
    downloadPDFPath = reportDocument.downloadPDFPath;
  const loading = reportAnnotation.loading,
    active = reportAnnotation.active,
    reportAnnotationLoading = reportAnnotation.loading,
    reportAnnotationChanges = reportAnnotation.pendingChanges;
  const issueLoading = issue.loading,
    issueChanges = issue.pendingChanges;
  let saveState = SaveButton.STATUS_DISABLED;
  if (reportAnnotationChanges && reportAnnotationLoading || issueChanges && issueLoading) {
    saveState = SaveButton.STATUS_IN_PROGRESS;
  } else if (reportAnnotationChanges && !reportAnnotationLoading || issueChanges && !issueLoading) {
    saveState = SaveButton.STATUS_PENDING;
  }
  let maxLength = 0;
  const transformedList = documentsButtonList.map(doc => {
    const displayLength = doc.displayName ? doc.displayName.length : 0;
    maxLength = Math.max(maxLength, displayLength);
    return _objectSpread(_objectSpread({}, doc), {}, {
      name: doc.displayName || '<no name provided>'
    });
  });
  const header = /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    className: "float-right t-ae-view-report-btn",
    onClick: handleViewReportClick
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "eye"
  }), " View Report"));
  const docsButton = /*#__PURE__*/React.createElement(PopoverButton, {
    width: maxLength * 10,
    header: header,
    selected: params.reportDocumentID,
    onSelect: handleReportDocumentClick,
    list: transformedList,
    testingIdentifier: "documents-button",
    title: "Documents"
  });
  const enableEditAnnotations = true;
  const editButton = /*#__PURE__*/React.createElement("div", {
    style: {
      marginLeft: 10
    }
  }, /*#__PURE__*/React.createElement(Button, {
    className: "t-ae-edit-report-btn",
    onClick: handleEditReportClick
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "pencil-alt"
  }), " Edit Report"));
  const buttonGroup = /*#__PURE__*/React.createElement(ButtonGroup, null, docsButton, editButton);
  const handleDownload = useCallback(() => {
    reportDocumentActions.serverRenderAnnotatedDocument(match.params.reportDocumentID);
  }, [reportDocumentActions, match.params.reportDocumentID]);
  const clearDownload = useCallback(() => {
    reportDocumentActions.clearDownloadPDFPath();
  }, [reportDocumentActions]);
  const pdfViewer = pdfPath ? /*#__PURE__*/React.createElement(PDF, {
    enableEditAnnotations: enableEditAnnotations,
    asyncDownloadPath: true,
    src: pdfPath,
    button: buttonGroup,
    title: displayName,
    containerMaxHeight: maxHeight,
    onAnnotationSelect: handleAnnotationClick,
    handleTransition: handleTransition,
    download: handleDownload,
    downloadPDFPath: downloadPDFPath,
    clearDownload: clearDownload
  }) : /*#__PURE__*/React.createElement("div", null, error ? "There was an error: ".concat(error) : /*#__PURE__*/React.createElement(Loader, {
    top: "25%"
  }));
  const removeMessage = selectableType === 'issue' ? 'issue annotation and all other data associated with this issue' : "".concat(selectableType, " annotation");
  const handlerRemove = () => removeItemWithConfirm(active,
  // pass in active
  removeMessage, ['id'],
  // always look to see if active has an id (which it always should...)
  () => serverDeleteAnnotation(active.id, selectableType));
  const selectedType = selectableType ? selectableType.charAt(0).toUpperCase() + selectableType.slice(1) : 'Annotation';
  const userCanEditIssue = canEditIssue || active.appCreatorID === userID;
  const footer = /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Button
  /* eslint-disable react/jsx-indent-props */, {
    disabled: !active.id || !userCanEditIssue,
    className: "float-left t-ae-remove",
    onClick: handlerRemove
    /* eslint-enable react/jsx-indent-props */
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "trash"
  }), " Remove ", selectedType), /*#__PURE__*/React.createElement(Button, {
    disabled: !active.id,
    className: "float-right t-ae-scroll",
    onClick: handlePageNavigation
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "crosshairs"
  }), " Scroll to ", selectedType));
  return /*#__PURE__*/React.createElement(Container, {
    fluid: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 9
  }, pdfViewer), /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 3
  }, /*#__PURE__*/React.createElement(Prompt, {
    when: (reportAnnotation === null || reportAnnotation === void 0 ? void 0 : reportAnnotation.pendingChanges) || (issue === null || issue === void 0 ? void 0 : issue.pendingChanges),
    message: location => handleTransition(() => hashHistory.replace(location.pathname))
  }), selectableType === 'issue' ? /*#__PURE__*/React.createElement(IssueEditor, {
    match: match,
    issueID: issue.active && issue.active.id ? issue.active.id : null,
    containerMaxHeight: maxHeight,
    footer: footer,
    width: "sm",
    saveState: saveState,
    handleSave: handleSave
  }) : /*#__PURE__*/React.createElement(AnnotationEditorDetailViewPanel, {
    match: match,
    loading: loading,
    annotation: active,
    type: selectableType,
    containerMaxHeight: maxHeight,
    handleSave: handleSave,
    onChange: handleChange,
    footer: footer,
    saveState: saveState
  }))));
};
ReportAnnotationEditor.propTypes = {
  annotationSelection: PropTypes.shape({
    selectableType: PropTypes.string,
    editMode: PropTypes.bool,
    defaultColor: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  annotationSelectionActions: PropTypes.shape({
    change: PropTypes.func,
    setAnnotationContext: PropTypes.func,
    clearSelection: PropTypes.func,
    select: PropTypes.func,
    setSelectableType: PropTypes.func,
    setDefaultColor: PropTypes.func,
    batchChange: PropTypes.func
  }).isRequired,
  documentsButtonList: PropTypes.arrayOf(PropTypes.shape({
    reportID: PropTypes.string
  })).isRequired,
  documentsButtonIndexQuery: PropTypes.shape({}).isRequired,
  issuePopoverReportDocumentActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  issue: PropTypes.shape({
    error: PropTypes.shape({
      response: PropTypes.shape({
        status: PropTypes.number
      })
    }),
    active: PropTypes.shape({
      content: PropTypes.shape({
        version: PropTypes.string
      }),
      id: PropTypes.string
    }),
    pendingChanges: PropTypes.bool,
    loading: PropTypes.bool
  }).isRequired,
  issueActions: PropTypes.shape({
    serverShow: PropTypes.func,
    serverPatch: PropTypes.func,
    showCancel: PropTypes.func,
    serverSave: PropTypes.func,
    undoChanges: PropTypes.func
  }).isRequired,
  issueType: PropTypes.shape({
    indexQuery: PropTypes.shape({}),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  issueTypeActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportDocumentID: PropTypes.string.isRequired
    }).isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  reportDocument: PropTypes.shape({
    pdfPath: PropTypes.string,
    downloadPDFPath: PropTypes.string,
    parentReportID: PropTypes.string,
    displayName: PropTypes.string,
    error: PropTypes.string,
    reportTypeID: PropTypes.string
  }).isRequired,
  reportDocumentActions: PropTypes.shape({
    serverRenderAnnotatedDocument: PropTypes.func,
    serverRenderReportDocument: PropTypes.func,
    clearDownloadPDFPath: PropTypes.func,
    reset: PropTypes.func
  }).isRequired,
  reportAnnotation: PropTypes.shape({
    loading: PropTypes.bool,
    pendingChanges: PropTypes.bool,
    list: PropTypes.arrayOf(PropTypes.shape({})),
    active: PropTypes.shape({
      id: PropTypes.string,
      issue: PropTypes.shape({}),
      appCreatorID: PropTypes.string,
      inkAnnotation: PropTypes.shape({}),
      infoAnnotation: PropTypes.shape({}),
      freeTextAnnotation: PropTypes.shape({}),
      highlightAnnotation: PropTypes.shape({})
    })
  }),
  reportAnnotationActions: PropTypes.shape({
    reset: PropTypes.func,
    serverCreate: PropTypes.func,
    undoChanges: PropTypes.func,
    serverDeleteAnnotation: PropTypes.func,
    serverSave: PropTypes.func,
    selectByAnnotationTypeID: PropTypes.func
  }).isRequired,
  reportTypeIssueTypeActions: PropTypes.shape({
    index: PropTypes.func,
    serverIndex: PropTypes.func
  }).isRequired,
  reportTypeIssueType: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape({})),
    indexQuery: PropTypes.shape({})
  }).isRequired,
  userID: PropTypes.string.isRequired,
  canEditIssue: PropTypes.bool.isRequired,
  canViewIssue: PropTypes.bool.isRequired,
  notificationActions: PropTypes.shape({
    info: PropTypes.func,
    error: PropTypes.func,
    dismiss: PropTypes.func
  }).isRequired
};
export { ReportAnnotationEditor as UnComposedAnnotationEditor };
export default entityComposition(ReportAnnotationEditor, ['annotationSelection', 'reportDocument', 'issue', 'issueType', 'reportAnnotation', 'reportTypeIssueType'], {
  canEditIssue: state => editPermissionSelector('issue')(state),
  canViewIssue: state => viewPermissionSelector('issue')(state),
  documentsButtonList: state => state.issuePopoverReportDocument.list,
  documentsButtonIndexQuery: state => state.issuePopoverReportDocument.indexQuery,
  userID: state => state.login.user.user_id
}, ['issuePopoverReportDocument', 'notification']);